import { useMemo, useState } from 'react'
import { useInfiniteQuery } from 'react-query'
import { useParams } from 'react-router'
import { getMonyPoolsDetailUsers } from 'services/fund'

interface IUser extends IWithDrawUser {
    isSelected: boolean
}
interface ISelectUser {
    name: string
    id: undefined | number
    memberId: undefined | number
}

/**
 * Members for the «دریافت کننده» / «پرداخت کننده» picker.
 *
 * This used to hit `/poolships/` once and render whatever came back. That
 * endpoint is cursor-paginated, so on any fund with more than one page of
 * members the picker silently listed only the first page — everyone after it
 * was unreachable unless you already knew the name and searched for it. Now we
 * walk the same cursor the fund member list uses and let the modal pull more
 * pages as it scrolls.
 */
export default function useGetUsers(
    setSelect: React.Dispatch<React.SetStateAction<ISelectUser | undefined>>,
    searchTransaction: string
) {
    const [selectedId, setSelectedId] = useState<number | undefined>()
    const { id } = useParams()

    // callers seed this with ' ' rather than '' — trim so a blank search asks
    // for the whole list instead of filtering on a space.
    const search = (searchTransaction || '').trim()

    const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
        useInfiniteQuery(
            ['select-modal-poolships', id, search],
            ({ pageParam = 1 }) =>
                getMonyPoolsDetailUsers(pageParam, id, search),
            {
                getNextPageParam: (lastPage, allPages) => {
                    const lastRecord = allPages[allPages.length - 1]?.next || ''
                    return lastRecord
                        ? new URL(lastRecord).searchParams.get('cursor')
                        : undefined
                },
                enabled: Boolean(id),
            }
        )

    // Selection lives here rather than on the rows: rebuilding the row list on
    // every fetched page used to wipe the highlight the moment another page
    // loaded in behind the modal.
    const users: Array<IUser> | undefined = useMemo(() => {
        if (!data) {
            return undefined
        }
        return data.pages
            .flatMap((page) => page.results)
            .map((item) => ({
                ...(item as unknown as IWithDrawUser),
                isSelected: item.id === selectedId,
            }))
    }, [data, selectedId])

    const handleSelect = (poolshipId: number) => {
        setSelectedId(poolshipId)
    }

    return {
        data: users,
        isLoading,
        handleSelect,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    }
}
